export type AgentModelTier = 'fast' | 'balanced' | 'powerful';

export type AgentProvider = 'openrouter' | 'azure';

export interface AgentModel {
  id: string;            // канонический id внутри приложения, вида "vendor/model"
  label: string;
  tier: AgentModelTier;
  usdPer1kInput: number;   // базовая цена провайдера (БЕЗ наценки) за 1k промпт-токенов, USD
  usdPer1kOutput: number;  // базовая цена провайдера (БЕЗ наценки) за 1k completion-токенов, USD
  vision: boolean;             // поддержка image-инпутов (мультимодальность)
  provider: AgentProvider;
  /** Имя деплоймента в Azure. Обязательно при provider === 'azure'. */
  deployment?: string;
  /** Куда уходим, если Azure недоступен. Обязательно при provider === 'azure'. */
  fallbackId?: string;
}

// Курируемый allowlist ТОЛЬКО tool-capable моделей. Ставки — приблизительные
// базовые цены OpenRouter (USD/1k токенов, ДО наценки CHAT_MARKUP из pricing.ts);
// сверить с актуальным прайсом через OpenRouter `/models` при онлайн-биллинге
// приоритет отдаётся реальному costUsd из ответа (см. computeChatCostUsd).
export const AGENT_MODELS: AgentModel[] = [
  {
    id: 'azure/gpt-5.4-mini',
    label: 'GPT-5.4 mini',
    tier: 'fast',
    // Retail-прайс Azure, polandcentral / Data Zone Standard (сверено 20.08.2026):
    // вход $0.825 за 1M токенов, выход $4.95 за 1M.
    usdPer1kInput: 0.000825,
    usdPer1kOutput: 0.00495,
    vision: true,
    provider: 'azure',
    deployment: 'gpt-5.4-mini',
    fallbackId: 'openai/gpt-5-mini',
  },
  {
    id: 'azure/gpt-5.6-sol',
    label: 'GPT-5.6 sol',
    tier: 'powerful',
    // ⚠️ Ставки НЕ сверены с прайс-листом: страница тарифов Azure отдаёт цены
    // скриптом, розничный прайс-API по polandcentral пуст, а отчёт по расходам
    // на кредитной подписке возвращает нули. Здесь — оценка в десять раз от
    // mini, типовое соотношение старшей модели к младшей. Перед тем как считать
    // по ним деньги, сверить в калькуляторе Azure.
    usdPer1kInput: 0.00825,
    usdPer1kOutput: 0.0495,
    vision: true,
    provider: 'azure',
    deployment: 'gpt-5.6-sol',
    fallbackId: 'openai/gpt-5',
  },
  { id: 'google/gemini-2.5-flash', label: 'Gemini Flash', tier: 'fast', usdPer1kInput: 0.0003, usdPer1kOutput: 0.0025, vision: true, provider: 'openrouter' },
  { id: 'anthropic/claude-haiku-4.5', label: 'Claude Haiku', tier: 'fast', usdPer1kInput: 0.001, usdPer1kOutput: 0.005, vision: true, provider: 'openrouter' },
  { id: 'openai/gpt-5-mini', label: 'GPT-5 mini', tier: 'fast', usdPer1kInput: 0.00025, usdPer1kOutput: 0.002, vision: true, provider: 'openrouter' },
  { id: 'anthropic/claude-sonnet-4.5', label: 'Claude Sonnet', tier: 'balanced', usdPer1kInput: 0.003, usdPer1kOutput: 0.015, vision: true, provider: 'openrouter' },
  { id: 'google/gemini-2.5-pro', label: 'Gemini Pro', tier: 'balanced', usdPer1kInput: 0.00125, usdPer1kOutput: 0.010, vision: true, provider: 'openrouter' },
  { id: 'openai/gpt-5', label: 'GPT-5', tier: 'balanced', usdPer1kInput: 0.00125, usdPer1kOutput: 0.010, vision: true, provider: 'openrouter' },
  { id: 'anthropic/claude-opus-4.1', label: 'Claude Opus', tier: 'powerful', usdPer1kInput: 0.015, usdPer1kOutput: 0.075, vision: true, provider: 'openrouter' },
];

export const AZURE_DEFAULT_MODEL_ID = 'azure/gpt-5.4-mini';

// Дефолт для новых чатов: Azure, потому что он оплачивается стартап-кредитом.
// resolveChatModel() сам уведёт запрос на OpenRouter, если Azure не настроен или лежит.
export const DEFAULT_MODEL_ID = AZURE_DEFAULT_MODEL_ID;

// Сложный монтаж пока уходит на OpenRouter: на Azure модель такого класса появится
// после выдачи квоты на gpt-5.4 / gpt-5.6-sol (заявки поданы 20.08.2026).
export const COMPLEX_EDIT_MODEL_ID = 'anthropic/claude-sonnet-4.5';

/**
 * Модель Canny для вошедших пользователей.
 *
 * Отдельная константа, а не общий дефолт: у Canny и редактора он был один, и
 * переключение одного молча меняло второй. Здесь маркетинговые задачи —
 * разбор бренда, тексты, промпты для роликов, — где качество формулировок и
 * есть весь продукт, поэтому берём старшую модель.
 *
 * Анонимное демо остаётся на mini намеренно: у него суточный потолок расхода,
 * и на старшей модели он выбирался бы в разы быстрее — демо умирало бы к обеду.
 */
export const CANNY_MODEL_ID = 'azure/gpt-5.6-sol';

export function getModelById(id: string): AgentModel | undefined {
  return AGENT_MODELS.find((m) => m.id === id);
}

export interface AgentModelRoute {
  requestedModelId: string;
  resolvedModelId: string;
  autoUpgraded: boolean;
  reason: 'selected' | 'complex-edit';
}

/**
 * Higgsfield-style model routing for the editor domain. The user's selected
 * model remains the baseline for quick questions and one-step adjustments;
 * multi-action editing briefs are promoted from a fast model to a stronger
 * tool-calling model. Balanced/powerful selections are always respected.
 */
export function routeAgentModel(requestedId: string | undefined, userText: string): AgentModelRoute {
  const requested = getModelById(requestedId ?? '') ?? getModelById(DEFAULT_MODEL_ID)!;
  const normalized = userText.toLocaleLowerCase('ru');
  const actionGroups = [
    /добав|встав|налож|созда/, /перемест|позици|масштаб|поверн/, /обреж|подреж|разреж|склей|смонтир/,
    /переход|появлен|исчезнов|анимац|ключев/, /музык|аудио|громк|duck|приглуш/,
    /текст|стикер|шаблон|субтитр|эффект|фильтр/, /найд|говор|речь|библиотек|содержим/,
    /сгенер|генерац|сториборд|референс/,
  ];
  const actionCount = actionGroups.filter((pattern) => pattern.test(normalized)).length;
  const chained = /\s(?:и|затем|после этого|потом)\s/.test(normalized) && actionCount >= 2;
  const complex = actionCount >= 3 || chained || normalized.length > 220;

  if (requested.tier === 'fast' && complex && getModelById(COMPLEX_EDIT_MODEL_ID)) {
    return {
      requestedModelId: requested.id,
      resolvedModelId: COMPLEX_EDIT_MODEL_ID,
      autoUpgraded: true,
      reason: 'complex-edit',
    };
  }
  return {
    requestedModelId: requested.id,
    resolvedModelId: requested.id,
    autoUpgraded: false,
    reason: 'selected',
  };
}
